import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { type Question } from '../../content';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { AssessmentHeader } from './AssessmentHeader';
import { AssessmentQuestionCard } from './AssessmentQuestionCard';
import { AssessmentMCOptions } from './AssessmentMCOptions';
import { AssessmentIntroScreen } from './AssessmentIntroScreen';
import { AssessmentCompleteScreen } from './AssessmentCompleteScreen';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { error as logError } from '../../utils/logger';
import type { AssessmentDomainId } from '../types';
interface AssessmentSessionProps {
  questions: Question[];
  domainId?: AssessmentDomainId;
  onComplete: () => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number) => void;
}
type SessionPhase = 'intro' | 'questions' | 'complete';
export function AssessmentSession({
  questions,
  domainId = 'rekenen',
  // fallback
  onComplete,
  onExit,
  onAnswer
}: AssessmentSessionProps) {
  const [phase, setPhase] = useState<SessionPhase>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({});
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<number>>(new Set());
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const startTimeRef = useRef(Date.now());
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const selectedOriginalIndex = selectedOptions[currentIndex] ?? null;
  // Global Timer — starts when entering questions phase
  useEffect(() => {
    if (phase !== 'questions') return;
    startTimeRef.current = Date.now();
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);
  // Escape key for exit
  useEscapeKey(() => {
    if (phase === 'questions') setShowExitConfirm(true);
  });
  // Navigation
  const handleNext = useCallback(() => {
    if (selectedOriginalIndex === null) return;
    // Record answer
    const isCorrect = selectedOriginalIndex === currentQuestion.correctAnswer;
    onAnswer(currentQuestion.id, isCorrect, selectedOriginalIndex);
    setAnsweredQuestions(prev => new Set(prev).add(currentIndex));
    if (isLastQuestion) {
      // Capture exact elapsed time before transitioning — the interval
      // only ticks every 1 s, so elapsedMs could be up to ~1 s behind.
      setElapsedMs(Date.now() - startTimeRef.current);
      setPhase('complete');
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [selectedOriginalIndex, currentQuestion, currentIndex, isLastQuestion, onAnswer]);
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);
  const handleSelect = useCallback((originalIndex: number) => {
    setSelectedOptions(prev => ({
      ...prev,
      [currentIndex]: originalIndex
    }));
  }, [currentIndex]);
  // Enter key to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase === 'questions' && e.key === 'Enter' && selectedOriginalIndex !== null && !showExitConfirm) {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, selectedOriginalIndex, showExitConfirm, handleNext]);
  if (!currentQuestion && phase === 'questions') {
    logError('AssessmentSession: No current question found');
    return null;
  }
  if (phase === 'intro') {
    return <AssessmentIntroScreen domainId={domainId} questionCount={questions.length} onStart={() => {
      setPhase('questions');
      startTimeRef.current = Date.now();
    }} onCancel={onExit} data-id="element-1165" />;
  }
  if (phase === 'complete') {
    return <AssessmentCompleteScreen questionCount={questions.length} elapsedMs={elapsedMs} onViewReport={onComplete} data-id="element-1166" />;
  }
  return <div className="min-h-screen bg-gradient-to-b from-indigo-50/80 to-slate-50 flex flex-col font-sans h-screen overflow-hidden" data-id="element-1167">
      <AssessmentHeader currentQuestion={currentIndex + 1} totalQuestions={questions.length} elapsedMs={elapsedMs} onExit={() => setShowExitConfirm(true)} onPrevious={handlePrevious} canGoPrevious={currentIndex > 0} answeredIndices={answeredQuestions} data-id="element-1168" />

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 mt-16 flex flex-col min-h-0 overflow-y-auto pb-24" data-id="element-1169">
        <div className="flex-1 flex flex-col relative justify-center" data-id="element-1170">
          <AnimatePresence mode="wait" data-id="element-1171">
            <motion.div key={currentQuestion.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} className="w-full" data-id="element-1172">
              <AssessmentQuestionCard question={currentQuestion} data-id="element-1173">
                <AssessmentMCOptions questionId={currentQuestion.id} options={currentQuestion.options || []} onSelect={handleSelect} selectedOriginalIndex={selectedOriginalIndex} data-id="element-1174" />
              </AssessmentQuestionCard>

              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mt-8 flex justify-center" data-id="element-1175">
                <Button size="xl" variant="primary" disabled={selectedOriginalIndex === null} onClick={handleNext} className="w-full sm:w-auto sm:min-w-[320px] shadow-lg rounded-2xl text-fluid-lg font-bold" data-id="element-1176">
                  <span className="mr-2" data-id="element-1177">
                    {isLastQuestion ? 'Toets Afronden' : 'Volgende'}
                  </span>
                  <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-1178" />
                </Button>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Assessment Exit Modal */}
        <AnimatePresence data-id="element-1179">
          {showExitConfirm && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" onClick={() => setShowExitConfirm(false)} data-id="element-1180" />
              <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.4
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1181">
                <Card className="max-w-md w-full p-8 shadow-2xl border border-slate-200 bg-white rounded-3xl" data-id="element-1182">
                  <div className="text-center" data-id="element-1183">
                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1184">
                      <AlertCircle className="w-7 h-7 text-amber-600" data-id="element-1185" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-1186">
                      Toets stoppen?
                    </h2>
                    <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-1187">
                      Je voortgang wordt opgeslagen als onvolledig. Je kunt
                      later opnieuw beginnen.
                    </p>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-left" data-id="element-1188">
                      <p className="text-sm text-slate-700 font-medium" data-id="element-1189">
                        Je hebt {answeredQuestions.size} van de{' '}
                        {questions.length} vragen beantwoord.
                      </p>
                    </div>
                    <div className="flex gap-3" data-id="element-1190">
                      <Button onClick={() => setShowExitConfirm(false)} variant="primary" size="lg" fullWidth data-id="element-1191">
                        Doorgaan
                      </Button>
                      <Button onClick={() => {
                    setShowExitConfirm(false);
                    onExit();
                  }} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-1192">
                        Stoppen
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>
    </div>;
}